import { useEffect, useState } from 'react';
import { configApi } from '@/services/config.api';

/**
 * Au mount, récupère le profil organisation publique et applique :
 *  - `--accent` + `--accent-hover` + `--accent-hex` : couleur de marque
 *    personnalisée (override du fallback `#3E63DD` de globals.css).
 *  - `<link rel="icon">` : favicon personnalisé (servi via un Blob URL
 *    puisque la route nécessite un token d'auth).
 *
 * Idempotent : les variables CSS restent sur leur dernière valeur posée.
 */
export function useBrandBootstrap(token: string | null): void {
  useEffect(() => {
    if (!token) return;
    let revokedUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const profile = await configApi.getOrganization(token);
        if (cancelled) return;

        if (profile.brandColor) {
          applyBrandColor(profile.brandColor);
        }

        if (profile.hasFavicon) {
          const blobUrl = await fetchAssetAsBlobUrl(token, 'favicon');
          if (!cancelled && blobUrl) {
            revokedUrl = blobUrl;
            applyFavicon(blobUrl);
          }
        }
      } catch {
        /* fallback aux valeurs par défaut - pas d'erreur bloquante */
      }
    })();

    return () => {
      cancelled = true;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [token]);
}

/** Convertit `#RRGGBB` en triple `H S% L%` pour assignation dans --accent. */
export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return null;
  const n = parseInt(match[1]!, 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function applyBrandColor(hex: string): void {
  const hsl = hexToHsl(hex);
  if (!hsl) return;
  const root = document.documentElement;
  // Hover = 8 points de luminance en moins (plus foncé), clamp ≥ 15 %.
  const hoverL = Math.max(15, hsl.l - 8);
  root.style.setProperty('--accent', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
  root.style.setProperty('--accent-hover', `${hsl.h} ${hsl.s}% ${hoverL}%`);
  root.style.setProperty('--accent-hex', hex);
}

async function fetchAssetAsBlobUrl(
  token: string,
  kind: 'logo' | 'favicon',
): Promise<string | null> {
  const res = await fetch(`/api/v1/config/organization/${kind}`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function applyFavicon(url: string): void {
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

/**
 * Hook dédié sidebar : retourne le Blob URL du logo de marque (ou null si
 * aucun logo / chargement en cours). Géré séparément de `useBrandBootstrap`
 * parce que le logo s'affiche dans le DOM React (re-render requis) alors
 * que le favicon est posé directement sur `<head>`.
 */
export function useBrandLogo(token: string | null, hasLogo: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!token || !hasLogo) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let blobUrl: string | null = null;
    (async () => {
      const next = await fetchAssetAsBlobUrl(token, 'logo');
      if (cancelled) {
        if (next) URL.revokeObjectURL(next);
        return;
      }
      blobUrl = next;
      setUrl(next);
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [token, hasLogo]);
  return url;
}
